import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Category } from '@/types'

interface ProductImage {
  image_url: string
  is_primary: boolean
  sort_order: number
}

interface CategoryWithPreview extends Category {
  preview_images?: {
    primary: string | null
    secondary: string | null
  }
}

export function useCategories() {
  return useQuery({
    queryKey: ['engitronix-categories'],
    queryFn: async () => {
      // Get categories with product count
      const { data: categories, error } = await supabase
        .from('engitronix_categories')
        .select(`
          *,
          products:engitronix_products(count)
        `)
        .order('sort_order', { ascending: true })

      if (error) throw error

      // Get first 2 products with images for each category
      const categoriesWithPreviews = await Promise.all(
        (categories || []).map(async (cat: Record<string, unknown>) => {
          const { data: products } = await supabase
            .from('engitronix_products')
            .select(`
              id,
              images:engitronix_product_images(image_url, is_primary, sort_order)
            `)
            .eq('category_id', cat.id as string)
            .eq('is_active', true)
            .order('sort_order', { ascending: true })
            .limit(2)

          let primaryImage: string | null = null
          let secondaryImage: string | null = null

          if (products && products.length > 0) {
            const firstProduct = products[0] as { images: ProductImage[] }
            // Sort first product's images
            const firstProductImages = (firstProduct.images || []).sort((a: ProductImage, b: ProductImage) => {
              if (a.is_primary && !b.is_primary) return -1
              if (!a.is_primary && b.is_primary) return 1
              return a.sort_order - b.sort_order
            })

            primaryImage = firstProductImages[0]?.image_url || null

            // Check if there's a second product
            if (products.length > 1) {
              const secondProduct = products[1] as { images: ProductImage[] }
              if (secondProduct.images?.length > 0) {
                // Use second product's first image
                const secondProductImages = (secondProduct.images || []).sort((a: ProductImage, b: ProductImage) => {
                  if (a.is_primary && !b.is_primary) return -1
                  if (!a.is_primary && b.is_primary) return 1
                  return a.sort_order - b.sort_order
                })
                secondaryImage = secondProductImages[0]?.image_url || null
              }
            } else if (firstProductImages.length > 1) {
              // Use first product's second image
              secondaryImage = firstProductImages[1]?.image_url || null
            }
          }

          const catProducts = cat.products as Array<{ count: number }> | undefined
          return {
            ...cat,
            product_count: catProducts?.[0]?.count ?? 0,
            preview_images: {
              primary: primaryImage,
              secondary: secondaryImage,
            },
          }
        })
      )

      return categoriesWithPreviews as CategoryWithPreview[]
    },
  })
}

export function useCategory(slug: string | undefined) {
  return useQuery({
    queryKey: ['engitronix-category', slug],
    queryFn: async () => {
      if (!slug) return null

      const { data, error } = await supabase
        .from('engitronix_categories')
        .select('*')
        .eq('slug', slug)
        .single()

      if (error) throw error
      return data as Category
    },
    enabled: !!slug,
  })
}
